// Tabulación de respuestas de "Diseña tu propio Learning" (pestaña
// Resumen de LearningAdminPage). Funciones puras: reciben las preguntas
// del Learning y las filas de learning_responses ya cargadas, y devuelven
// los conteos listos para mostrar. Sin acceso a red ni a Date.now().
//
// Formato de `answers` (lo arma LearningPublicPage y lo guarda tal cual
// la Edge Function learning-public): { [question.id]: valor }, donde
//   - open_text       → string
//   - multiple_choice → el TEXTO de la opción elegida (no su índice)
//   - scale           → número entre 1 y scale_max

export type LearningQuestionType = 'multiple_choice' | 'open_text' | 'scale';

export interface LearningQuestion {
  id: string;
  type: LearningQuestionType;
  prompt: string;
  options: string[];
  scale_max: number;
  required: boolean;
}

export interface LearningResponseRow {
  id: string;
  email: string;
  nombre_apellido: string;
  sucursal: string | null;
  departamento: string | null;
  answers: Record<string, string | number>;
  submitted_at: string;
}

// Valor del filtro para "respuestas sin sucursal/departamento" (null en la
// base). '' significa "Todas / Todos".
export const FILTER_NONE = '__sin_valor__';

export interface ResponseFilters {
  sucursal: string;
  departamento: string;
}

function matchesFilter(value: string | null, filter: string): boolean {
  if (filter === '') return true;
  if (filter === FILTER_NONE) return value === null || value.trim() === '';
  return value === filter;
}

export function filterResponses(responses: LearningResponseRow[], filters: ResponseFilters): LearningResponseRow[] {
  return responses.filter(r => matchesFilter(r.sucursal, filters.sucursal) && matchesFilter(r.departamento, filters.departamento));
}

// Opciones del selector de filtro: las configuradas en el Learning, más las
// que aparecen en respuestas (pueden haberse quitado del Learning después),
// sin duplicados; y FILTER_NONE al final si alguna respuesta no trae valor.
export function filterOptions(configured: string[], values: (string | null)[]): { options: string[]; hasNone: boolean } {
  const seen = new Set<string>();
  const options: string[] = [];
  for (const v of [...configured, ...values]) {
    if (v === null) continue;
    const t = v.trim();
    if (t === '' || seen.has(t)) continue;
    seen.add(t);
    options.push(t);
  }
  const hasNone = values.some(v => v === null || v.trim() === '');
  return { options, hasNone };
}

// Una respuesta cuenta como contestada si existe y no es texto vacío.
export function isAnswered(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

// expires_at es una fecha de calendario ('2027-01-31') y el Learning sigue
// activo hasta el FINAL de ese día en hora de Ecuador (UTC−05:00). Devuelve
// el primer instante (ms UTC) en que ya está vencido: 00:00 en Ecuador del
// día siguiente = 05:00 UTC. Se toma solo la parte AAAA-MM-DD, así funciona
// igual con fecha sola o con timestamp a medianoche UTC. null si no hay
// fecha o no tiene ese formato.
//
// Espejo de expiryInstantMs() en supabase/functions/learning-public/expiry.ts
// (la Edge Function no puede importar de src/); el test compara ambas.
export function learningExpiryInstantMs(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(expiresAt);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1, 5, 0, 0, 0);
}

// Mismo criterio que la Edge Function learning-public para aceptar envíos:
// status 'activo' y expires_at (si existe) todavía no terminado en Ecuador.
export function isLearningAvailable(learning: { status: string; expires_at: string | null }, now: Date): boolean {
  if (learning.status !== 'activo') return false;
  if (!learning.expires_at) return true;
  const limit = learningExpiryInstantMs(learning.expires_at);
  // Formato inesperado: mismo criterio de respaldo que la Edge Function.
  if (limit === null) return !(new Date(learning.expires_at) < now);
  return now.getTime() < limit;
}

export interface OptionCount {
  label: string;
  count: number;
  pct: number; // sobre quienes respondieron la pregunta, entero redondeado
}

export interface ResponseEntry<T> {
  response: LearningResponseRow;
  value: T;
}

export type QuestionSummary =
  | {
      kind: 'multiple_choice';
      question: LearningQuestion;
      answered: number;
      options: OptionCount[];
      // Respuestas cuyo texto ya no coincide con ninguna opción actual
      // (las opciones se editaron después de recibirlas).
      orphans: OptionCount[];
    }
  | {
      kind: 'scale';
      question: LearningQuestion;
      answered: number;
      average: number | null;
      min: number | null;
      max: number | null;
      distribution: { value: number; count: number }[]; // 1..scale_max
      outOfRange: number; // valores fuera de 1..scale_max actual
      entries: ResponseEntry<number>[];
    }
  | {
      kind: 'open_text';
      question: LearningQuestion;
      answered: number;
      entries: ResponseEntry<string>[];
    };

function pct(n: number, total: number): number {
  return total === 0 ? 0 : Math.round((n / total) * 100);
}

// Del más reciente al más antiguo.
function byNewest(a: LearningResponseRow, b: LearningResponseRow): number {
  return Date.parse(b.submitted_at) - Date.parse(a.submitted_at);
}

// Opciones actuales: sin espacios sobrantes, sin líneas vacías (el editor
// guarda una opción por línea, y una línea en blanco queda como '') y sin
// duplicados.
export function cleanOptions(options: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const o of options ?? []) {
    const t = o.trim();
    if (t === '' || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

export function summarizeQuestion(question: LearningQuestion, responses: LearningResponseRow[]): QuestionSummary {
  const answeredRows = responses.filter(r => isAnswered(r.answers?.[question.id]));
  const answered = answeredRows.length;

  if (question.type === 'multiple_choice') {
    // Conteo por texto de opción. Se compara sin espacios de los extremos
    // porque la página pública guarda el texto tal como estaba en el
    // editor (con posibles espacios sobrantes).
    const current = cleanOptions(question.options);
    const counts = new Map<string, number>(current.map(o => [o, 0]));
    const orphanCounts = new Map<string, number>();
    for (const r of answeredRows) {
      const text = String(r.answers[question.id]).trim();
      if (counts.has(text)) counts.set(text, (counts.get(text) ?? 0) + 1);
      else orphanCounts.set(text, (orphanCounts.get(text) ?? 0) + 1);
    }
    return {
      kind: 'multiple_choice',
      question,
      answered,
      options: current.map(label => ({ label, count: counts.get(label) ?? 0, pct: pct(counts.get(label) ?? 0, answered) })),
      orphans: Array.from(orphanCounts.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([label, count]) => ({ label, count, pct: pct(count, answered) })),
    };
  }

  if (question.type === 'scale') {
    const scaleMax = Number.isInteger(question.scale_max) && question.scale_max >= 1 ? question.scale_max : 10;
    const entries: ResponseEntry<number>[] = [];
    for (const r of [...answeredRows].sort(byNewest)) {
      const n = Number(r.answers[question.id]);
      if (Number.isFinite(n)) entries.push({ response: r, value: n });
    }
    const values = entries.map(e => e.value);
    const distribution = Array.from({ length: scaleMax }, (_, i) => ({
      value: i + 1,
      count: values.filter(v => v === i + 1).length,
    }));
    const inRange = distribution.reduce((a, d) => a + d.count, 0);
    return {
      kind: 'scale',
      question,
      answered: entries.length,
      average: values.length ? values.reduce((a, v) => a + v, 0) / values.length : null,
      min: values.length ? Math.min(...values) : null,
      max: values.length ? Math.max(...values) : null,
      distribution,
      outOfRange: values.length - inRange,
      entries,
    };
  }

  // open_text
  return {
    kind: 'open_text',
    question,
    answered,
    entries: [...answeredRows].sort(byNewest).map(r => ({ response: r, value: String(r.answers[question.id]).trim() })),
  };
}

export function summarizeLearning(questions: LearningQuestion[], responses: LearningResponseRow[]): QuestionSummary[] {
  return (questions ?? [])
    .filter(q => q.type === 'multiple_choice' || q.type === 'scale' || q.type === 'open_text')
    .map(q => summarizeQuestion(q, responses));
}
